import type {
  ServiceDefinitionFormSubmissionValue,
  ServiceDefinitionFormValue,
} from '@/services/api';

export interface ServiceDefinitionFormRow {
  key: string;
  id?: string;
  code: string;
  name: string;
  description: string;
  aliasesText: string;
  minAcdText: string;
  maxAcdText: string;
}

export type ServiceDefinitionSubmissionResult =
  | { ok: true; definitions: ServiceDefinitionFormSubmissionValue[] }
  | { ok: false; error: string };

export function createServiceDefinitionFormRows(
  definitions: ServiceDefinitionFormValue[],
  createKey: () => string = () => crypto.randomUUID()
): ServiceDefinitionFormRow[] {
  const source = definitions.length > 0
    ? definitions
    : [{ id: '', code: '', name: '', description: null, aliases: [], minAcdSeconds: null, maxAcdSeconds: null }];
  return source.map((definition) => ({
    key: createKey(),
    id: definition.id || undefined,
    code: definition.code,
    name: definition.name,
    description: definition.description ?? '',
    aliasesText: definition.aliases.join('\n'),
    minAcdText: definition.minAcdSeconds == null ? '' : String(definition.minAcdSeconds),
    maxAcdText: definition.maxAcdSeconds == null ? '' : String(definition.maxAcdSeconds),
  }));
}

// Returns undefined for invalid text; empty text is an open bound.
function parseAcdBound(value: string): number | null | undefined {
  const text = value.trim();
  if (!text) return null;
  if (!/^\d{1,4}$/.test(text)) return undefined;
  const seconds = Number(text);
  return seconds <= 3_600 ? seconds : undefined;
}

function normalizeCode(value: string): string {
  return value.normalize('NFKC').toLocaleLowerCase('en-US').replace(/\s+/g, ' ');
}

export function buildServiceDefinitionSubmission(
  rows: ServiceDefinitionFormRow[]
): ServiceDefinitionSubmissionResult {
  if (rows.length < 1 || rows.length > 100) {
    return { ok: false, error: 'Provide between one and 100 Service Definitions.' };
  }
  const normalizedCodes = new Set<string>();
  const definitions: ServiceDefinitionFormSubmissionValue[] = [];
  for (const row of rows) {
    const code = row.code.trim();
    const name = row.name.trim();
    if (!code || !name) {
      return { ok: false, error: 'Each Service Definition needs a code and a name.' };
    }
    const normalizedCode = normalizeCode(code);
    if (normalizedCodes.has(normalizedCode)) {
      return { ok: false, error: 'Each service code must be unique.' };
    }
    normalizedCodes.add(normalizedCode);
    const description = row.description.trim();
    const aliases = row.aliasesText
      .split('\n')
      .map((alias) => alias.trim())
      .filter(Boolean);
    const minAcdSeconds = parseAcdBound(row.minAcdText ?? '');
    const maxAcdSeconds = parseAcdBound(row.maxAcdText ?? '');
    if (minAcdSeconds === undefined || maxAcdSeconds === undefined) {
      return { ok: false, error: 'An ACD range bound must be a whole number of seconds from 0 to 3600.' };
    }
    if (minAcdSeconds !== null && maxAcdSeconds !== null && minAcdSeconds >= maxAcdSeconds) {
      return { ok: false, error: `The ACD range of ${code} must start below its end.` };
    }
    definitions.push({
      ...(row.id ? { id: row.id } : {}),
      code,
      name,
      description: description || null,
      aliases,
      minAcdSeconds,
      maxAcdSeconds,
    });
  }
  const ranged = definitions.filter((definition) => (
    definition.minAcdSeconds != null || definition.maxAcdSeconds != null
  ));
  for (let left = 0; left < ranged.length; left += 1) {
    for (let right = left + 1; right < ranged.length; right += 1) {
      const a = ranged[left]!;
      const b = ranged[right]!;
      if (
        (a.minAcdSeconds ?? 0) < (b.maxAcdSeconds ?? Number.POSITIVE_INFINITY)
        && (b.minAcdSeconds ?? 0) < (a.maxAcdSeconds ?? Number.POSITIVE_INFINITY)
      ) {
        return { ok: false, error: `The ACD ranges of ${a.code} and ${b.code} overlap.` };
      }
    }
  }
  return { ok: true, definitions };
}
